import { Moment } from 'moment';

import { Injectable, Injector } from '@angular/core';

import { Session } from './session';

import { moment, IMoment } from '../lib/moment';
import { isLocaleFrench } from '../lib/i18n';

/*
  Format keys for standard formatting.

  Date: Jun 22 or 06/16/26
  Time: 5:11 PM
  TimeSec: 5:11:30 PM
  Zone: PKT
  DateTimeSecZone: Jun 22, 5:11:30 PM PKT
*/
@Injectable({ providedIn: 'root' })
export class MomentFormatter {
  private session: Session;

  constructor(private injector: Injector) { }

  public isSameYear(dateTime: IMoment | Moment): boolean {
    return dateTime.isSame(moment(), 'year');
  }

  public zone(): string {
    return 'z';
  }

  public time(format: string): string {
    const H = this.shortHourFormat(this.is24HourFormat());
    const HH = this.longHourFormat(this.is24HourFormat());
    const meridiem = !this.is24HourFormat() ? ' A' : '';
    const zone = format.indexOf('z') > 0 ? ' z' : '';
    if (format.indexOf('SEC') > 0 || format.indexOf('ss') > 0) return `${HH}:mm:ss${meridiem}${zone}`;
    return `${H}:mm${meridiem}${zone}`;
  }

  public date(isSameYear: boolean): string {
    if (isSameYear) return 'MMM DD';
    return 'MMM DD YYYY';
  }

  // french locale follows 24 Hour format
  public is24HourFormat(): boolean {
    return isLocaleFrench();
  }

  // with leading 0, (00 - 23)
  public longHourFormat(is24HourFormat: boolean): string {
    return is24HourFormat ? 'HH' : 'hh';
  }

  // without leading 0, (0 - 23)
  public shortHourFormat(is24HourFormat: boolean): string {
    return is24HourFormat ? 'H' : 'h';
  }

  public format(dateTime: IMoment | Moment, format: string, useUserTimezone: boolean = true): string {
    if (!moment.isMoment(dateTime)) return '';
    if (useUserTimezone && !dateTime.tz()) {
      this.session = this.injector.get(Session);
      dateTime = (dateTime.clone() as IMoment).mapTz(this.session.getUserTimeZone());
    }

    const _format = format.toUpperCase();
    if (_format.includes('DATE') || _format.includes('TIME') || _format.includes('ZONE')) {
      const isSameYear = this.isSameYear(dateTime);
      let momentFormat = '';
      if (_format.startsWith('DATE')) momentFormat = this.date(isSameYear);
      if (_format.startsWith('TIME')) momentFormat = this.time(_format);
      if (_format.startsWith('ZONE')) momentFormat = this.zone();
      if (_format.indexOf('TIME') > 0) momentFormat += `, ${this.time(_format)}`;
      if (_format.indexOf('ZONE') > 0) momentFormat += ` ${this.zone()}`;

      return dateTime.format(momentFormat);
    }
    return dateTime.format(format);
  }

  /*
   @param {boolean} [friendlyDateFormat=false] - An optional param with a default value
   responsible for user friendly dates i.e. "Today, 12:15pm", "Yesterday, 1:20pm", "12/20, 02:10pm"
   */
  public formatLastUpdatedTimeForFVTabularView(
      dateTime: IMoment,
      useUserTimezone: boolean = true,
      friendlyDateFormat: boolean = false,
    ): string {
    if (!moment.isMoment(dateTime)) return '';
    if (useUserTimezone && !dateTime.tz()) {
      this.session = this.injector.get(Session);
      dateTime = (dateTime.clone() as IMoment).mapTz(this.session.getUserTimeZone());
    }
    let format: string = 'MMM DD YYYY';
    if (friendlyDateFormat) {
      if (dateTime.isSame(moment(), 'day')) format = '[Today,] hh:mm A';
      else if (dateTime.isSame(moment().subtract(1, 'day'), 'day')) format = '[Yesterday,] hh:mm A';
      else if (dateTime.isSame(moment(), 'year')) format = 'MM/DD, hh:mm A';
    } else {
      if (dateTime.isSame(moment(), 'day')) format = 'h:mm A';
      else if (dateTime.isSame(moment(), 'year')) format = 'MMM DD, h:mm A';
    }
    return dateTime.format(format);
  }
}
